import React, { useState, useEffect } from "react";
import {
	Box,
	Paper,
	Typography,
	List,
	ListItem,
	ListItemText,
	ListItemIcon,
	Badge,
	Chip,
	Button,
	Alert,
	Divider,
} from "@mui/material";
import { Bell, Mail, Clock, Eye, Video } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAcademicYear } from "../../contexts/AcademicYearContext";
import { ROLES } from "../../config/supabase";
import { applyCurrentAcademicYearNotificationFilter } from "../../utils/notifications";
import {
	GAME_RESULTS_UPLOAD_PATH,
	isEditedVideoNotification,
} from "../../utils/gameResultNotifications";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";

dayjs.extend(relativeTime);

const UserNotifications = ({ user, supabase }) => {
	const { currentAcademicYear, loading: academicYearLoading } = useAcademicYear();
	const isTeacher = user?.profile?.role === ROLES.SCHOOL_TEACHER;
	const [notifications, setNotifications] = useState([]);
	const [loading, setLoading] = useState(true);
	const [unreadCount, setUnreadCount] = useState(0);
	const navigate = useNavigate();

	useEffect(() => {
		if (isTeacher && academicYearLoading) return;
		fetchUnreadNotifications();
	}, [user.id, currentAcademicYear, isTeacher, academicYearLoading]);

	const fetchUnreadNotifications = async () => {
		try {
			setLoading(true);
			if (isTeacher && !currentAcademicYear) {
				setNotifications([]);
				setUnreadCount(0);
				return;
			}
			let query = supabase
				.from("notifications")
				.select(
					`
					*,
					sender:profiles!sender_id(full_name, email)
				`
				)
				.eq("recipient_id", user.id)
				.eq("is_read", false)
				.order("created_at", { ascending: false })
				.limit(5);

			if (isTeacher) {
				query = applyCurrentAcademicYearNotificationFilter(
					query,
					currentAcademicYear
				);
			}

			const { data, error } = await query;

			setNotifications(data || []);
			setUnreadCount(data?.length || 0);
		} catch (err) {
			console.error("Error fetching unread notifications:", err);
		} finally {
			setLoading(false);
		}
	};

	const markAsRead = async (notificationId) => {
		try {
			const { error } = await supabase
				.from("notifications")
				.update({ is_read: true, read_at: new Date().toISOString() })
				.eq("id", notificationId);

			if (error) throw error;

			// Update local state
			setNotifications((prev) => prev.filter((n) => n.id !== notificationId));
			setUnreadCount((prev) => Math.max(0, prev - 1));
		} catch (err) {
			console.error("Error marking notification as read:", err);
		}
	};

	const handleNotificationClick = async (notification) => {
		await markAsRead(notification.id);
		if (isEditedVideoNotification(notification)) {
			navigate(GAME_RESULTS_UPLOAD_PATH);
		}
	};

	const markAllAsRead = async () => {
		try {
			const unreadIds = notifications.map((n) => n.id);

			if (unreadIds.length === 0) return;

			const { error } = await supabase
				.from("notifications")
				.update({ is_read: true, read_at: new Date().toISOString() })
				.in("id", unreadIds);

			if (error) throw error;

			// Clear all notifications from dashboard
			setNotifications([]);
			setUnreadCount(0);
		} catch (err) {
			console.error("Error marking all notifications as read:", err);
		}
	};

	const formatDate = (date) => {
		return dayjs(date).fromNow();
	};

	const handleViewAllNotifications = () => {
		navigate("/my-notifications");
	};

	if (loading) {
		return (
			<Paper
				elevation={0}
				sx={{ p: 2, border: "1px solid #e5e7eb", borderRadius: "8px" }}>
				<Typography>Loading notifications...</Typography>
			</Paper>
		);
	}

	if (notifications.length === 0) {
		return (
			<Paper
				elevation={0}
				sx={{ p: 2, border: "1px solid #e5e7eb", borderRadius: "8px" }}>
				<Box
					sx={{
						display: "flex",
						alignItems: "center",
						justifyContent: "space-between",
						mb: 2,
					}}>
					<Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
						<Bell size={24} />
						<Typography variant="h6">Notifications</Typography>
					</Box>
					<Button
						size="small"
						variant="outlined"
						startIcon={<Eye size={16} />}
						onClick={handleViewAllNotifications}>
						View All
					</Button>
				</Box>
				<Typography variant="body2" color="text.secondary">
					No unread notifications.
				</Typography>
			</Paper>
		);
	}

	return (
		<Paper
			elevation={0}
			sx={{ p: 2, border: "1px solid #e5e7eb", borderRadius: "8px" }}>
			<Box
				sx={{
					display: "flex",
					alignItems: "center",
					justifyContent: "space-between",
					mb: 2,
				}}>
				<Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
					<Badge badgeContent={unreadCount} color="error">
						<Bell size={24} />
					</Badge>
					<Typography variant="h6">New Notifications</Typography>
					<Chip
						label={`${unreadCount} unread`}
						size="small"
						color="primary"
						variant="outlined"
					/>
				</Box>

				<Box sx={{ display: "flex", gap: 1 }}>
					<Button size="small" onClick={markAllAsRead} variant="outlined">
						Mark all read
					</Button>
					<Button
						size="small"
						variant="outlined"
						startIcon={<Eye size={16} />}
						onClick={handleViewAllNotifications}>
						View All
					</Button>
				</Box>
			</Box>

			<List sx={{ p: 0 }}>
				{notifications.map((notification, index) => (
					<React.Fragment key={notification.id}>
						<ListItem
							sx={{
								px: 0,
								py: 1,
								bgcolor: "rgba(59, 130, 246, 0.05)",
								borderRadius: 1,
								cursor: "pointer",
								"&:hover": {
									bgcolor: "rgba(59, 130, 246, 0.1)",
								},
							}}
							onClick={() => handleNotificationClick(notification)}>
							<ListItemIcon sx={{ minWidth: 40 }}>
								{isEditedVideoNotification(notification) ? (
									<Video size={20} color="#3b82f6" />
								) : (
									<Mail size={20} color="#3b82f6" />
								)}
							</ListItemIcon>
							<ListItemText
								primary={
									<Box
										sx={{
											display: "flex",
											justifyContent: "space-between",
											alignItems: "flex-start",
										}}>
										<Typography
											variant="subtitle2"
											sx={{
												fontWeight: "bold",
												color: "text.primary",
											}}>
											{notification.subject}
										</Typography>
										<Typography
											variant="caption"
											color="text.secondary"
											sx={{
												display: "flex",
												alignItems: "center",
												gap: 0.5,
												ml: 1,
											}}>
											<Clock size={12} />
											{formatDate(notification.created_at)}
										</Typography>
									</Box>
								}
								secondary={
									<Box>
										<Typography
											variant="body2"
											color="text.secondary"
											sx={{
												overflow: "hidden",
												textOverflow: "ellipsis",
												display: "-webkit-box",
												WebkitLineClamp: 2,
												WebkitBoxOrient: "vertical",
												mt: 0.5,
											}}>
											{notification.message}
										</Typography>
										{notification.sender && (
											<Typography
												variant="caption"
												color="text.secondary"
												sx={{ mt: 0.5 }}>
												From: {notification.sender.full_name}
											</Typography>
										)}
									</Box>
								}
							/>
						</ListItem>
						{index < notifications.length - 1 && <Divider sx={{ my: 0.5 }} />}
					</React.Fragment>
				))}
			</List>
		</Paper>
	);
};

export default UserNotifications;
